import { handleActions, createAction } from "redux-actions";
import fp from "lodash/fp";
import { axios } from "../cores/axiosWrapper";
import queryString from "../cores/queryString";
import { getAsset } from "./asset";
import { getAssetAPI } from "./apis/asset";

/**
 * Search domain 목록 조회
 * @param {*} apiEndpoint
 * @param {*} params : ex) ?domain=storage&externalId=bucketName
 * @returns
 */
export const listRegisteredDomainsAPI = (apiEndpoint, projectId, params) => {
    return axios.get(`${apiEndpoint}/v2/integrated-search/domains`, { params, headers: { projectId } });
};

/**
 * Search domain 등록
 * @param {} apiEndpoint
 * @param {*} domain : ex) storage | asset
 * @param {*} externalId  ex) storage인 경우 bucket 이름, asset인 경우 projectId
 * @returns
 */
const registerDomainAPI = (apiEndpoint, domain, externalId) => {
    return axios.post(`${apiEndpoint}/v2/integrated-search/domains`, {
        domain,
        externalId,
    });
};

/**
 * Search domain 등록 해제
 * @param {} apiEndpoint
 * @param {*} domainId
 * @returns
 */
const unregisterDomainAPI = (apiEndpoint, domainId) => {
    return axios.delete(`${apiEndpoint}/v2/integrated-search/domains/${domainId}`);
};

const getSearchAPI = (apiEndpoint, projectId, params) => {
    return axios.get(`${apiEndpoint}/v2/integrated-search`, { params, headers: { projectId } });
};

const getAccessibleBucketsAPI = (apiEndpoint, params) => {
    return axios.get(`${apiEndpoint}/me/resource-buckets`);
};

const SET_DOMAIN_V2 = "SET_DOMAIN_V2";
const SET_VALIDATION_ERROR_V2 = "SET_VALIDATION_ERROR_V2";
const RESET_ADVANCED_SEARCH_V2 = "RESET_ADVANCED_SEARCH_V2";
const RESET_SEARCH_FILTER_V2 = "RESET_SEARCH_FILTER_V2";
const GET_ACCESSIBLE_BUCKETS_PENDING_V2 = "GET_ACCESSIBLE_BUCKETS_PENDING_V2";
const GET_ACCESSIBLE_BUCKETS_SUCCESS_V2 = "GET_ACCESSIBLE_BUCKETS_SUCCESS_V2";
const GET_ACCESSIBLE_BUCKETS_FAILURE_V2 = "GET_ACCESSIBLE_BUCKETS_FAILURE_V2";
const GET_SEARCH_PENDING_V2 = "GET_SEARCH_PENDING_V2";
const GET_SEARCH_SUCCESS_V2 = "GET_SEARCH_SUCCESS_V2";
const GET_SEARCH_FAILURE_V2 = "GET_SEARCH_FAILURE_V2";
const UPDATE_SEARCH_QUERY_V2 = "UPDATE_SEARCH_QUERY_V2";
const UPDATE_ASSETS_PAGE_V2 = "UPDATE_ASSETS_PAGE_V2";
const UPDATE_ASSETS_SIZE_V2 = "UPDATE_ASSETS_SIZE_V2";
const UPDATE_FILTERED_DATA_V2 = "UPDATE_FILTERED_DATA_V2";
const UPDATE_DETAIL_FILTER_V2 = "UPDATE_DETAIL_FILTER_V2";
const RESET_SEARCH_DATA_V2 = "RESET_SEARCH_DATA_V2";

const GET_LIST_REGISTERED_DOMAIN_PENDING_V2 = "GET_LIST_REGISTERED_DOMAIN_PENDING_V2";
const GET_LIST_REGISTERED_DOMAIN_SUCCESS_V2 = "GET_LIST_REGISTERED_DOMAIN_SUCCESS_V2";
const GET_LIST_REGISTERED_DOMAIN_FAILURE_V2 = "GET_LIST_REGISTERED_DOMAIN_FAILURE_V2";
const ADD_LIST_REGISTERED_DOMAIN_SUCCESS_V2 = "ADD_LIST_REGISTERED_DOMAIN_SUCCESS_V2";
const ADD_LIST_REGISTERED_DOMAIN_FAILURE_V2 = "ADD_LIST_REGISTERED_DOMAIN_FAILURE_V2";
const DELETE_LIST_REGISTERED_DOMAIN_SUCCESS_V2 = "DELETE_LIST_REGISTERED_DOMAIN_SUCCESS_V2";
const DELETE_LIST_REGISTERED_DOMAIN_FAILURE_V2 = "DELETE_LIST_REGISTERED_DOMAIN_FAILURE_V2";

const initialState = {
    error: false,
    filter: {
        asset: {
            project: [],
            name: "", // name
            createdBy: "", // owner
            id: "",
            status: "",
            mediaType: [],
            createdAt: [], // createdAt ex) startAt ~ endAt
            type: [],
            duration: [], // duration ex) beginDuration~endDuration
            extensions: [],
            tags: [],
            fileMeta: [],
        },
        storage: {
            project: [],
            storageClass: "",
            lastModifiedDate: [], //lastModified ex)startDuration~endDuration
        },
    },
    search: {
        q: "",
        domain: "all",
        version: 0,
    },
    validationErrors: {
        name: "required",
    },
    searchData: {
        error: null,
        pending: false,
        data: [], //전체 데이터
        filteredData: [], //전체 데이터 중 선택된 탭, 필터링 조건에 맞는 데이터
        totalCount: 0,
        page: 0,
        size: 20,
    },
    accessibleBuckets: {
        error: null,
        pending: false,
        data: [],
    },
    registeredDomain: {
        asset: { domains: [], pending: false, error: null },
        storage: { domains: [], pending: false, error: null },
    },
};

const cloneInitialState = fp.cloneDeep(initialState);

export default handleActions(
    {
        [SET_VALIDATION_ERROR_V2]: (state, action) => {
            return { ...state };
        },
        [RESET_SEARCH_FILTER_V2]: (state, action) => {
            return {
                ...state,
                filter: {
                    ...cloneInitialState.filter,
                },
            };
        },
        [RESET_ADVANCED_SEARCH_V2]: (state, action) => {
            return { ...initialState };
        },
        [SET_DOMAIN_V2]: (state, action) => {
            return {
                ...state,
                search: { ...state.search, domain: action.payload },
            };
        },
        [GET_ACCESSIBLE_BUCKETS_PENDING_V2]: (state) => {
            return { ...state, accessibleBuckets: { ...cloneInitialState.accessibleBuckets, pending: true } };
        },
        [GET_ACCESSIBLE_BUCKETS_SUCCESS_V2]: (state, action) => {
            return {
                ...state,
                accessibleBuckets: { ...state.accessibleBuckets, pending: false, data: action.payload },
            };
        },
        [GET_ACCESSIBLE_BUCKETS_FAILURE_V2]: (state, action) => {
            return {
                ...state,
                accessibleBuckets: { ...state.accessibleBuckets, pending: false, error: action.payload },
            };
        },
        [GET_SEARCH_PENDING_V2]: (state, action) => {
            return {
                ...state,
                searchData: {
                    ...cloneInitialState.searchData,
                    totalCount: state.searchData?.totalCount,
                    pending: true,
                    limit: state.searchData.limit,
                },
                //filter: { ...cloneInitialState.filter },
                search: { ...state.search, version: state.search.version + 1 },
            };
        },
        [GET_SEARCH_FAILURE_V2]: (state) => {
            return {
                ...state,
                searchData: { ...state.searchData, pending: false, error: true },
            };
        },
        [GET_SEARCH_SUCCESS_V2]: (state, action) => {
            return {
                ...state,
                searchData: {
                    ...state.searchData,
                    pending: false,
                    data: action.payload.results,
                    filteredData: action.payload.results,
                    totalCount: action.payload.totalCount,
                },
            };
        },
        [RESET_SEARCH_DATA_V2]: (state, { payload }) => {
            return {
                ...state,
                searchData: {
                    ...cloneInitialState.searchData,
                    totalCount: payload?.preventDeleteTotalCount ? state.searchData.totalCount : 0,
                },
                search: { ...state.search, q: "" },
            };
        },
        [UPDATE_ASSETS_SIZE_V2]: (state, action) => {
            return {
                ...state,
                searchData: {
                    ...state.searchData,
                    size: action.payload,
                },
            };
        },
        [UPDATE_ASSETS_PAGE_V2]: (state, action) => {
            return {
                ...state,
                searchData: {
                    ...state.searchData,
                    page: action.payload,
                },
            };
        },
        [UPDATE_SEARCH_QUERY_V2]: (state, action) => {
            return {
                ...state,
                search: {
                    ...state.search,
                    q: action.payload,
                },
            };
        },
        [UPDATE_FILTERED_DATA_V2]: (state, action) => {
            return {
                ...state,
                searchData: {
                    ...state.searchData,
                    filteredData: action.payload,
                },
            };
        },
        [UPDATE_DETAIL_FILTER_V2]: (state, action) => {
            if (action.payload.type === "storage") {
                return {
                    ...state,
                    filter: { ...state.filter, storage: { ...state.filter.storage, ...action.payload.data } },
                };
            } else if (action.payload.type === "asset") {
                return {
                    ...state,
                    filter: { ...state.filter, asset: { ...state.filter.asset, ...action.payload.data } },
                };
            }
            return state;
        },
        [GET_LIST_REGISTERED_DOMAIN_PENDING_V2]: (state, action) => {
            return {
                ...state,
                registeredDomain: {
                    ...cloneInitialState.registeredDomain,
                    [action.payload.domain]: {
                        ...cloneInitialState.registeredDomain[action.payload.domain],
                        pending: true,
                    },
                },
            };
        },
        [GET_LIST_REGISTERED_DOMAIN_SUCCESS_V2]: (state, action) => {
            return {
                ...state,
                registeredDomain: {
                    ...state.registeredDomain,
                    [action.payload.domain]: { domains: action.payload.data, pending: false },
                },
            };
        },
        [GET_LIST_REGISTERED_DOMAIN_FAILURE_V2]: (state, action) => {
            return {
                ...state,
                registeredDomain: {
                    ...state.registeredDomain,
                    [action.payload.domain]: { error: action.payload.data, pending: false },
                    pending: false,
                },
            };
        },
        [ADD_LIST_REGISTERED_DOMAIN_SUCCESS_V2]: (state, action) => {
            return {
                ...state,
                registeredDomain: {
                    ...state.registeredDomain,
                    [action.payload.domain]: {
                        domains: [...state.registeredDomain[action.payload.domain].domains, action.payload.data],
                        pending: false,
                    },
                },
            };
        },
        [ADD_LIST_REGISTERED_DOMAIN_FAILURE_V2]: (state, action) => {
            return {
                ...state,
                registeredDomain: {
                    ...state.registeredDomain,
                    [action.payload.domain]: {
                        error: action.payload.data,
                        pending: false,
                    },
                },
            };
        },
        [DELETE_LIST_REGISTERED_DOMAIN_SUCCESS_V2]: (state, action) => {
            return {
                ...state,
                registeredDomain: {
                    ...state.registeredDomain,
                    [action.payload.domain]: {
                        domains: state.registeredDomain[action.payload.domain]?.domains.filter(
                            (domain) => domain.id !== action.payload.data,
                        ),
                        pending: false,
                    },
                },
            };
        },
        [DELETE_LIST_REGISTERED_DOMAIN_FAILURE_V2]: (state, action) => {
            return {
                ...state,
                registeredDomain: {
                    ...state.registeredDomain,
                    [action.payload.domain]: {
                        ...state.registeredDomain[action.payload.domain],
                        //error: action.payload.data,
                        pending: false,
                    },
                },
            };
        },
    },
    initialState,
);

export const setValidationError = createAction(SET_VALIDATION_ERROR_V2);
export const updateSearchQuery = createAction(UPDATE_SEARCH_QUERY_V2);

export const listRegisteredDomains = (domain, externalId) => (dispatch, getState) => {
    const { stage, project } = getState();

    dispatch({ type: GET_LIST_REGISTERED_DOMAIN_PENDING_V2, payload: { domain } });

    return new Promise((resolve, reject) => {
        listRegisteredDomainsAPI(stage.endpoint, project.id, { domain, externalId })
            .then((response) => {
                dispatch({
                    type: GET_LIST_REGISTERED_DOMAIN_SUCCESS_V2,
                    payload: { domain, data: response.data.results },
                });
                resolve(response.data.results);
            })
            .catch((error) => {
                dispatch({
                    type: GET_LIST_REGISTERED_DOMAIN_FAILURE_V2,
                    payload: { domain, data: error },
                });
                reject(error);
            });
    });
};

export const addRegisteredDomains = (domain, externalId) => (dispatch, getState) => {
    const { stage } = getState();

    return new Promise((resolve, reject) => {
        registerDomainAPI(stage.endpoint, domain, externalId)
            .then((response) => {
                dispatch({
                    type: ADD_LIST_REGISTERED_DOMAIN_SUCCESS_V2,
                    payload: { domain, data: response.data },
                });
                resolve(response.data);
            })
            .catch((error) => {
                dispatch({
                    type: DELETE_LIST_REGISTERED_DOMAIN_FAILURE_V2,
                    payload: { domain, data: error },
                });
                reject(error);
            });
    });
};

export const deleteRegisteredDomains = (domain, projectId) => (dispatch, getState) => {
    const { stage, project } = getState();

    return new Promise((resolve, reject) => {
        unregisterDomainAPI(stage.endpoint, projectId ?? project.id)
            .then((response) => {
                dispatch({
                    type: DELETE_LIST_REGISTERED_DOMAIN_SUCCESS_V2,
                    payload: { domain, data: projectId },
                });
                resolve(response.data);
            })
            .catch((error) => {
                console.error(error);
                dispatch({
                    type: DELETE_LIST_REGISTERED_DOMAIN_FAILURE_V2,
                    payload: { domain, data: error },
                });
                reject(error);
            });
    });
};
